import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Form, InputNumber, Select, message, Popconfirm, Tag, Spin
} from 'antd';
import { 
  LineChartOutlined, StopOutlined, PlusOutlined 
} from '@ant-design/icons';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const MarketControl = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [activeManipulations, setActiveManipulations] = useState([]);
  const [history, setHistory] = useState([]);
  const [pairs, setPairs] = useState(['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT', 'ADAUSDT']);

  const fetchManipulations = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/market-control');
      setActiveManipulations(res.active || []);
      setHistory(res.history || []);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors du chargement des contrôles de marché");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManipulations();
    // Refresh every 30s
    const interval = setInterval(fetchManipulations, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCreate = async (values) => {
    try {
      setLoading(true);
      await api.post('/admin/market-control', values);
      message.success("Mouvement de marché initié avec succès");
      form.resetFields();
      fetchManipulations();
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.error || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async (id) => {
    try {
      setLoading(true);
      await api.delete(`/admin/market-control/${id}`);
      message.success("Mouvement de marché arrêté");
      fetchManipulations();
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'arrêt du mouvement");
    } finally {
      setLoading(false);
    }
  };

  const activeColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Paire', dataIndex: 'pair', key: 'pair' },
    { 
      title: 'Pourcentage', 
      dataIndex: 'percentage', 
      key: 'percentage',
      render: (val) => (
        <Tag color={parseFloat(val) >= 0 ? 'success' : 'error'}>
          {parseFloat(val) > 0 ? '+' : ''}{val}%
        </Tag>
      )
    },
    { 
      title: 'Heure de Fin', 
      dataIndex: 'end_time', 
      key: 'end_time',
      render: (val) => new Date(val).toLocaleTimeString()
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Popconfirm title="Arrêter ce mouvement ?" onConfirm={() => handleStop(record.id)}>
          <Button danger icon={<StopOutlined />}>Arrêter</Button>
        </Popconfirm>
      )
    }
  ];

  const historyColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Paire', dataIndex: 'pair', key: 'pair' },
    { 
      title: 'Pourcentage', 
      dataIndex: 'percentage', 
      key: 'percentage',
      render: (val) => (
        <Tag color={parseFloat(val) >= 0 ? 'success' : 'error'}>
          {parseFloat(val) > 0 ? '+' : ''}{val}%
        </Tag>
      )
    },
    { 
      title: 'Créé le', 
      dataIndex: 'created_at', 
      key: 'created_at',
      render: (val) => new Date(val).toLocaleString()
    },
    {
      title: 'Statut',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => active ? <Tag color="processing">Actif</Tag> : <Tag color="default">Terminé</Tag>
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
            <LineChartOutlined className="mr-2 text-blue-500" />
            Contrôle du Marché
          </h1>
          <p className="text-gray-400">Manipulez artificiellement le prix d'un actif pour une durée définie.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 bg-gray-800 border-gray-700" title={<span className="text-white">Lancer un mouvement</span>}>
          <Form form={form} layout="vertical" onFinish={handleCreate}>
            <Form.Item 
              name="pair" 
              label={<span className="text-gray-300">Paire (ex: BTCUSDT)</span>} 
              rules={[{ required: true }]}
            >
              <Select placeholder="Sélectionnez une paire">
                {pairs.map(p => <Option key={p} value={p}>{p}</Option>)}
              </Select>
            </Form.Item>
            
            <Form.Item 
              name="percentage" 
              label={<span className="text-gray-300">Pourcentage (%)</span>} 
              rules={[{ required: true }]}
              help={<span className="text-gray-500">Ex: 0.5 pour +0.5%, -1 pour -1%</span>}
            >
              <InputNumber className="w-full" step={0.1} />
            </Form.Item>

            <Form.Item 
              name="durationMinutes" 
              label={<span className="text-gray-300">Durée (Minutes)</span>} 
              rules={[{ required: true }]}
              initialValue={15}
            >
              <InputNumber className="w-full" min={1} max={1440} />
            </Form.Item>

            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading} className="w-full bg-blue-600">
              Activer le Mouvement
            </Button>
          </Form>
        </Card>

        <div className="lg:col-span-2 flex flex-col gap-6">
          <Card className="bg-gray-800 border-gray-700" title={<span className="text-white flex items-center"><Spin spinning={loading} size="small" className="mr-2"/> Mouvements Actifs</span>}>
            <Table 
              dataSource={activeManipulations} 
              columns={activeColumns} 
              rowKey="id" 
              pagination={false}
              className="custom-table"
              locale={{ emptyText: 'Aucun mouvement actif' }}
            />
          </Card>

          <Card className="bg-gray-800 border-gray-700" title={<span className="text-white">Historique Récent</span>}>
            <Table 
              dataSource={history} 
              columns={historyColumns} 
              rowKey="id" 
              pagination={{ pageSize: 5 }}
              className="custom-table"
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MarketControl;
